import type { Weekdag } from '@kennisbank/shared';

/**
 * Datumvoorstel voor een reeks intervisiebijeenkomsten (besluit Jos 2 nov
 * 2026). Alleen een voorstel: de beheerder schuift of schrapt daarna losse
 * datums, en de server krijgt de definitieve lijst.
 *
 * Rekent in UTC op YYYY-MM-DD, zodat de zomertijdwissel geen dag verschuift.
 */

const WEEKDAG_NR: Record<Weekdag, number> = {
  maandag: 1,
  dinsdag: 2,
  woensdag: 3,
  donderdag: 4,
  vrijdag: 5,
};

function parse(d: string): Date {
  const [j, m, dag] = d.split('-').map(Number);
  return new Date(Date.UTC(j!, m! - 1, dag!));
}

function fmt(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** Eerste `dag` op of na `vanaf`. */
function eersteOpOfNa(vanaf: Date, dag: Weekdag | undefined): Date {
  const uit = new Date(vanaf);
  if (!dag) return uit;
  while (uit.getUTCDay() !== WEEKDAG_NR[dag]) uit.setUTCDate(uit.getUTCDate() + 1);
  return uit;
}

export interface PlanOpties {
  start: string;
  eind: string;
  intervalWeken: number;
  voorkeursdag?: Weekdag;
  /** Geen bijeenkomsten van t/m tot; daarna herstart het ritme (besluit Q15). */
  pauze?: { van: string; tot: string };
}

export function planDatums(o: PlanOpties): string[] {
  if (!o.start || !o.eind || o.intervalWeken < 1) return [];
  const eind = parse(o.eind);
  const pauzeVan = o.pauze?.van ? parse(o.pauze.van) : null;
  const pauzeTot = o.pauze?.tot ? parse(o.pauze.tot) : null;
  const uit: string[] = [];
  let d = eersteOpOfNa(parse(o.start), o.voorkeursdag);
  // ponytail: harde grens tegen een eindeloze lus bij rare invoer; 52 is ook
  // het maximum dat de server aanneemt.
  while (d <= eind && uit.length < 52) {
    if (pauzeVan && pauzeTot && d >= pauzeVan && d <= pauzeTot) {
      const naPauze = new Date(pauzeTot);
      naPauze.setUTCDate(naPauze.getUTCDate() + 1);
      d = eersteOpOfNa(naPauze, o.voorkeursdag);
      continue;
    }
    uit.push(fmt(d));
    d = new Date(d);
    d.setUTCDate(d.getUTCDate() + o.intervalWeken * 7);
  }
  return uit;
}

/**
 * Waarschuwing, geen regel. ponytail: juli en augustus als grove benadering;
 * de echte zomervakantie verschilt per regio en jaar. Precies maken als een
 * datum in juni of september hier ten onrechte doorheen glipt.
 */
export function inZomer(datum: string): boolean {
  const m = Number(datum.slice(5, 7));
  return m === 7 || m === 8;
}

const DAGNAMEN = ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag'];

export function dagnaam(datum: string): string {
  return DAGNAMEN[parse(datum).getUTCDay()]!;
}
